import "./styles.scss";

import { useNavigate } from "react-router-dom";

import Parser from "@helpers/parser";
import { Text, InfoBlock, Button, Icon } from "@fragments";
import { UserAvatar } from "@components/Community";
import {
  IconSchool,
  IconPhoto,
  IconArrowUpRight,
  IconCalendarStats,
  IconBuildingCommunity,
  IconClockHour4,
  IconUsers,
} from "@tabler/icons-react";

import MentoringStatusChip from "../components/MentoringStatusChip";
import MentoringTimeline from "../components/MentoringTimeline";
import useCommunityMembersPreview from "../hooks/useCommunityMembersPreview";
import { buildMentoringTimeline } from "../helpers/mentoringTimeline";

/**
 * Creator — subscribed mentorings list (§6.11). A responsive grid of program
 * cards: banner, status pill, attached community + member avatars, and a
 * two-lane progress (mentoring + split windows) with the time left on each.
 */
export default function MyMentoringsView({ enrollments, loading, onAttach }) {
  const navigate = useNavigate();

  return (
    <div className="my-mentorings px-4">
      <div className="mm-header">
        <Text.h2 left={<IconSchool />} weight={500} className="mb-1">
          Minhas mentorias
        </Text.h2>
        <Text.span contrast={6} style={{ paddingLeft: "44px" }}>
          Acompanhe os programas de mentoria em que você participa e o status de
          cada um.
        </Text.span>
      </div>

      {loading ? (
        <div className="mm-grid">
          {[0, 1, 2].map((i) => (
            <MentoringCardSkeleton key={i} index={i} />
          ))}
        </div>
      ) : enrollments.length === 0 ? (
        <div className="mm-empty">
          <InfoBlock
            title="Você ainda não participa de nenhuma mentoria"
            description="Quando você for convidado ou se inscrever em um programa de mentoria, ele aparecerá aqui."
          />
        </div>
      ) : (
        <div className="mm-grid">
          {enrollments.map((enrollment, index) => (
            <MentoringCard
              key={enrollment.id}
              enrollment={enrollment}
              index={index}
              onOpen={() => navigate(`/mentoring/${enrollment.id}`)}
              onAttach={onAttach}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MentoringCard({ enrollment, index, onOpen, onAttach }) {
  const { program, community, status } = enrollment;
  const canAttachCommunity = status === "awaiting_community";

  return (
    <div className="mm-card" style={{ "--i": index }} onClick={onOpen}>
      <div className="mm-card-banner">
        {program?.banner?.url ? (
          <img src={program.banner.url} alt={program.name} loading="lazy" />
        ) : (
          <div className="mm-card-banner-fallback">
            <IconPhoto size={26} />
          </div>
        )}
        <span className="mm-card-status">
          <MentoringStatusChip status={status} />
        </span>
        <span className="mm-card-open" aria-hidden="true">
          <IconArrowUpRight size={16} />
        </span>
      </div>

      {/* Info block — surface-10, pulled up to overlap the banner (§ design). */}
      <div className="mm-card-info">
        <Text.h3 contrast={9} weight={500}>
          {program?.name}
        </Text.h3>

        {community ? (
          <div className="mm-card-community-card is-linked">
            <div className="mm-card-community-media">
              {community.banner_url ? (
                <img src={community.banner_url} alt={community.name} loading="lazy" />
              ) : (
                <span className="mm-card-community-media-fallback">
                  <IconBuildingCommunity size={18} />
                </span>
              )}
            </div>
            <div className="mm-card-community-text">
              <Text.h5 contrast={7} weight={500}>
                {community.name}
              </Text.h5>
              <MembersPreview communityId={community.id} />
            </div>
          </div>
        ) : canAttachCommunity ? (
          <div
            className="mm-card-community-card is-action"
            onClick={(e) => {
              e.stopPropagation();
              onAttach?.(enrollment.id);
            }}
          >
            <Icon.Sphere children={              <IconBuildingCommunity size={16} />}/>
            <Text.span contrast={2} weight={500}>
              Anexar comunidade
            </Text.span>
            <IconArrowUpRight size={16} className="mm-card-community-cta" />
          </div>
        ) : (
          <div className="mm-card-community-card is-muted">
            <span className="mm-card-community-icon">
              <IconBuildingCommunity size={16} />
            </span>
            <Text.span contrast={5} maxLines={1}>
              Sem comunidade vinculada
            </Text.span>
          </div>
        )}

        <div className="mm-card-foot">
          {buildMentoringTimeline(enrollment.timestamps) ? (
            <MentoringTimeline timestamps={enrollment.timestamps} compact />
          ) : (
            <StatusHint status={status} />
          )}
        </div>

        <Button
          text="Ver mentoria"
          color="inverse"
          endIcon={<IconArrowUpRight size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          fullWidth
          preventLoading
        />
      </div>
    </div>
  );
}

// Stacked avatars of the attached community's members + the total count. Renders
// a slim skeleton while loading so the card doesn't jump.
function MembersPreview({ communityId }) {
  const { avatars, total, loading } = useCommunityMembersPreview(communityId);

  if (loading) {
    return (
      <div className="mm-members is-loading">
        <div className="mm-members-stack">
          {[0, 1, 2].map((i) => (
            <span className="mm-avatar-skel skel" style={{ "--i": i }} key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!total) return null;

  return (
    <div className="mm-members">
      {avatars.length > 0 && (
        <div className="mm-members-stack">
          {avatars.map((member, i) => (
            <span className="mm-avatar" style={{ "--i": i }} key={member.id}>
              <UserAvatar user={member} size={24} />
            </span>
          ))}
        </div>
      )}
      <Text.span contrast={5} left={<IconUsers size={13} />}>
        {Parser.number(total)} membros
      </Text.span>
    </div>
  );
}

// Status hint shown when there's no timeline yet (awaiting_community, pending).
function StatusHint({ status }) {
  if (status === "awaiting_community") {
    return (
      <Text.span contrast={6} left={<IconBuildingCommunity size={14} />}>
        Vincule sua comunidade
      </Text.span>
    );
  }
  if (status === "pending") {
    return (
      <Text.span contrast={5} left={<IconClockHour4 size={14} />}>
        Aguardando análise
      </Text.span>
    );
  }
  return (
    <Text.span contrast={5} left={<IconCalendarStats size={14} />}>
      —
    </Text.span>
  );
}

function MentoringCardSkeleton({ index }) {
  return (
    <div className="mm-card is-skeleton" style={{ "--i": index }}>
      <div className="mm-card-banner skel" />
      <div className="mm-card-info">
        <div className="skel skel-line" style={{ width: "70%" }} />
        <div className="skel skel-line" style={{ width: "100%", height: 46 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 8 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 34 }} />
      </div>
    </div>
  );
}
